import { Autocomplete, Avatar, Box, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, Grid, IconButton, Radio, RadioGroup, SelectChangeEvent, TextField, Tooltip, Typography, Dialog, DialogContentText, Alert, Snackbar } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import React, { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import CustomTextField from '../FormFields/CustomTextField';
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import GroupedCategorySelect from '../FormFields/GroupedCategorySelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import CustomCheckbox from '../FormFields/CustomCheckbox';

import { ApiStatus } from "../../utils/api.types";
import { fetchAccountsByName, fetchBuyerOrSupplierByName } from '../../store/accountThunk';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCurrencies } from '../../store/currencyStore/currencyThunk';
import { recordExpensePayment, fetchExpenseCategories, fetchExpenseVendors } from '../../store/expenseStore/expenseThunk';
import InfoIcon from '@mui/icons-material/Info';
import PeopleAltTwoToneIcon from '@mui/icons-material/PeopleAltTwoTone';
import moment from 'moment';
import { Utils, VocuherTypes } from '../../utils/common-utils';
import { deepOrange, deepPurple, green, pink, blue, teal } from "@mui/material/colors";

import { toast } from 'react-toastify';
import { CashInflowPayment } from '../../utils/cashinflow.types';
import { ExpenseFormDataNew } from '../../utils/expense.types';

interface DialogAddExpenseFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
}

const DialogAddExpenseForm: FC<DialogAddExpenseFormProps> = ({ onClose, open, title }) => {

  const { status } = useAppSelector(state => state.expense);
  const { categoryDropdownItems, categories } = useAppSelector(state => state.expense);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { banks } = useAppSelector(state => state.banks);
  const dispatch = useAppDispatch();

  const [instrumentDisabled, setInstrumentDisabled] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date(Date.now()));
  const [instrumentDate, setInstrumentDate] = useState<Date>(new Date(Date.now()));
  const [selectedToBankAccount, setSelectedToBankAccount] = useState('');
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [toBankAccountOptions, setToBankAccountOptions] = useState<IOptions[]>([]);
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  const [paymentMode, setPaymentMode] = useState('CASH');
  const [isCash, setIsCash] = useState(false);

  const [paymentModeOptions, setPaymentModeOptions] = useState<IOptions[]>([
    { label: 'CASH', value: 'CASH' },
    { label: 'CHEQUE', value: 'CHEQUE' },
    { label: 'UPI', value: 'UPI' },
    { label: 'CARD', value: 'CARD' },
    { label: 'NETBANKING', value: 'NETBANKING' },
  ]);

  const [currencyOptions, setCurrencyOptions] = useState<IOptions[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [paymentToOrFromAccount, setPaymentToOrFromAccount] = useState<any>();
  const [newOption, setNewOption] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPartySelectionLoading, setIsPartySelectionLoading] = useState(false);
  
  const [closedSelect, setClosedSelect] = useState(false);

  // Expense specific states
  const [selectedExpenseCategory, setSelectedExpenseCategory] = useState('');
  const [expenseStatus, setExpenseStatus] = useState('BOOKED');

  const [expenseStatusOptions, setExpenseStatusOptions] = useState<IOptions[]>([
    { label: 'Booked', value: 'BOOKED' },
    { label: 'Paid', value: 'PAID' }
  ]);

  // Schema to validate expense form
  const schema = yup.object().shape({
    paymentFrom: yup.string().required("Vendor/Supplier is required."),
    paymentDate: yup.string().required("Payment date is required."),
    paymentMode: yup.string().required("Payment mode is required."),
    currency: yup.string().required("Currency is required."),
    amount: yup.string().required("Amount is required."),
    expenseCategory: yup.string().required("Expense category is required."),
  });

  const methods = useForm<any>({ mode: 'onChange', reValidateMode: 'onChange', resolver: yupResolver(schema) });
  const { control, register } = methods;

  // Load initial data
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      try {
        if (selectedAccount && isMounted) {
          await dispatch(fetchExpenseCategories({ flat: false }));
          await dispatch(fetchExpenseVendors({ accountId: selectedAccount._id }));
          // await dispatch(fetchBanks(selectedAccount._id));
          // await dispatch(fetchCurrencies());
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };

    if (open) {
      loadInitialData();
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, open, selectedAccount]);


  useEffect(() => {
    let isMounted = true;

    const getBankDetails = async () => {
      try {
        if (selectedAccount?._id && isMounted) {
          const toBanks: any = await dispatch(fetchBanks(selectedAccount._id));
          if (isMounted && toBanks.payload?.banks?.length > 0) {
            const bankOptions = toBanks.payload.banks.map((c: any) => ({
              label: c.accountName + '-' + c.bankName,
              value: c._id
            }));
            setFromBankAccountOptions(bankOptions);

            const firstBank = toBanks.payload.banks[0];
            methods.setValue('fromBankAccount', firstBank._id);
            setSelectedFromBankAccount(firstBank._id);
          }
        }
      } catch (error) {
        console.error('Error fetching banks:', error);
      }
    };

    const getCurrencies = async () => {
      try {
        if (!currencyOptions.length && isMounted) {
          const data: any = await dispatch(fetchCurrencies());
          if (isMounted && data.payload) {
            setCurrencyOptions(data.payload.map((c: any) => ({
              label: c.code,
              value: c._id
            })));
          }
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      }
    };

    const setInitialData = () => {
      if (isMounted) {
        methods.setValue('paymentDate', paymentDate);
        methods.setValue('instrumentDate', instrumentDate);
      }
    };

    if (open && selectedAccount?._id) {
      getCurrencies();
      getBankDetails();
      setInitialData();
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, open, selectedAccount?._id]);

  // Handle payment mode change
  useEffect(() => {
    if (paymentMode === 'CASH') {
      setIsCash(true);
      setInstrumentDisabled(true);
      setSelectedToBankAccount('');
      setSelectedFromBankAccount('');
    } else {
      setIsCash(false);
      setInstrumentDisabled(false);
    }
  }, [paymentMode]);

  //Handle date range picker
  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'paymentDate') {
        setPaymentDate(newValue);
      } else if (name === 'instrumentDate') {
        setInstrumentDate(newValue);
      } 
      if (name) {
        methods.setValue(name, newValue);
      }
    }
  };

  const onChangeHandler = (_: any, value: any) => {
    setNewOption('');
    setIsLoading(true);
    setPaymentToOrFromAccount(value);
  };

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setPaymentToOrFromAccount('');
    setNewOption('');
    const _title = 'supplier';
    const _querystring = `name=${value}&searchFor=${_title?.toLocaleLowerCase()}`
    await dispatch(fetchBuyerOrSupplierByName(_querystring)).unwrap().then((data) => {
      if(data) {
        setOptions(data.accounts);
      }
    });
  };


  // Function to get a random color from an array
  const getRandomColor = () => {
    const colors = [pink[500], green[500], blue[500], deepOrange[500], deepPurple[500], teal[500]];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const handleRenderOption = (e: any, b: any, c: any) => {
    // console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    return <div {...e} className='auto-item' key={Math.random()} style={{alignItems: 'center'}}>
      <div className='img-wrapper' style={{marginLeft: 10}}>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          sx={{bgcolor: getRandomColor() }}
          style={{
            width: "35px",
            height: "35px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name.toString().substr(0,1).toUpperCase()}</Avatar>
      </div>
      <div style={{marginLeft: 10}}>
        <b style={{'marginRight': '5px'}}>

          <p style={{fontSize:13}}>
            {b.legalName} 
            {b.isAgent && 
              <Chip 
                size='small' 
                style={{
                  height:18, float: 'right', borderRadius:7,color:'#49a563',backgroundColor:'#edf6ef',
                  fontSize:11,marginTop:2,marginRight:3, marginLeft:3
                }} 
                label={<>Agent</>}
              />
            }
            {b.isVendor && 
              <Chip 
                size='small' 
                style={{
                  height:18, float: 'right', borderRadius:7,color:'#49a563',backgroundColor:'#edf6ef',
                  fontSize:11,marginTop:2,marginRight:3, marginLeft:3
                }} 
                label={<>Vendor</>}
              />
            }
            {b.isClient && 
              <Chip 
                size='small' 
                style={{
                  height:18, float: 'right', borderRadius:7,color:'#49a563',backgroundColor:'#edf6ef',
                  fontSize:11,marginTop:2,marginRight:3, marginLeft:3
                }} 
                label={<>Customer</>}
              />
            }
          </p>
        </b> 
        {!b.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>}
      </div>
    </div>
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let val = (event.target as HTMLInputElement).value;
    if (val == 'Cash') {
      setIsCash(true);
    } else {
      setIsCash(false);
    }
    setPaymentMode((event.target as HTMLInputElement).value);
  };


  const onCloseHandler = () => {
    setClosedSelect(true);
  };

  const onOpenHandler = () => {
    setClosedSelect(false);
  };

  // Handle form submission
  const onSubmit = async (formData: any) => {

    const data = methods.getValues();
    try {
      setFormSubmitted(true);

      let addPaymentReqObj: ExpenseFormDataNew = {
        "account": selectedAccount._id,
        "paymentTo": paymentToOrFromAccount?._id,
        "expenseCategory": selectedExpenseCategory,
        "paymentDate": new Date(paymentDate).getTime(),
        "amount": data.amount,
        "currency": "62a4b0ceb628fb10ac97ce0e", // data.currency,
        "paymentStatus": expenseStatus,
        "toBankAccount": selectedToBankAccount,
        "paymentMode": paymentMode.toUpperCase(),
        "instrument": paymentMode.toUpperCase(),
        "instrumentId": data.instrumentId,
        "instrumentDate": new Date(paymentDate).getTime(),
        "fromBankAccount": selectedFromBankAccount,
        "remarks": data.remark,
      };

      // const expenseData = {
      //   paymentTo: paymentToOrFromAccount?._id,
      //   amount: parseFloat(formData.amount),
      //   expenseType: formData.expenseCategory,
      //   paymentMode: formData.paymentMode,
      //   paymentDate: paymentDate.toISOString(),
      //   paymentStatus: expenseStatus,
      //   currency: formData.currency,
      //   remarks: formData.remarks || '',
      //   ...(formData.instrumentType && { instrument: formData.instrumentType }),
      //   ...(formData.instrumentId && { instrumentId: formData.instrumentId }),
      //   ...(formData.instrumentDate && { instrumentDate: instrumentDate.toISOString() }),
      //   ...(formData.reference1 && { ref1: formData.reference1 }),
      //   ...(formData.reference2 && { ref2: formData.reference2 }),
      //   ...(formData.reference3 && { ref3: formData.reference3 }),
      //   ...(selectedFromBankAccount && { fromBankAccount: selectedFromBankAccount }),
      //   ...(selectedToBankAccount && { toBankAccount: selectedToBankAccount }),
      // };

      console.log(addPaymentReqObj);

      await dispatch(recordExpensePayment(addPaymentReqObj)).unwrap();

      // Reset form
      methods.reset();
      setPaymentToOrFromAccount(null);
      setSelectedExpenseCategory('');
      setExpenseStatus('PAID');
      setPaymentMode('CASH');
      // setAmountReceived(0);

      onClose(true);
    } catch (error: any) {
      console.error('Error creating expense payment:', error);
    } finally {
      setFormSubmitted(false);
    }
  };

  const handleClose = () => {
    methods.reset();
    setPaymentToOrFromAccount(null);
    setSelectedExpenseCategory('');
    setExpenseStatus('PAID');
    setPaymentMode('CASH');
    onClose(false);
  };

  return (
      <div className="drawer-form-container">
        <FormProvider {...methods}>
          <Box
            sx={{ 
              display: "flex", 
              flexDirection: "column", 
              height: "100vh", 
              padding: "2px 0 0 2px",
              position: "relative"
            }}
            component="form"
            noValidate>
            <DialogTitle>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: '40px' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                  {title}
                </Typography>
                {(isPartySelectionLoading) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#059669' }} />
                    <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.8rem' }}>
                      {isPartySelectionLoading ? 'Loading party details...' : 'Loading invoices...'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogTitle>

            <DialogContent sx={{ 
              padding: '16px', 
              paddingBottom: '100px',
              maxHeight: 'calc(100vh - 200px)', 
              overflowY: 'auto',
              backgroundColor: '#fafafa',
              flex: 1
            }}>
              <Grid container spacing={2} style={{paddingTop: '20px'}}>
                
                {/* Transaction Details Section */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    p: 2,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                          <Controller
                            name={'paymentTo'}
                            control={control}
                            render={() => (
                              <Autocomplete
                                id={'paymentTo'}
                                freeSolo
                                value={paymentToOrFromAccount ?? ""}
                                onChange={onChangeHandler}
                                onInputChange={onInputChangeHandler}
                                renderOption={handleRenderOption}
                                onClose={onCloseHandler}
                                onOpen={onOpenHandler}
                                options={options}
                                getOptionLabel={option => option.legalName || option}
                                renderInput={(params) => (
                                  <CustomTextField 
                                    {...params} 
                                    value={paymentToOrFromAccount ?? ""} 
                                    name="paymentFrom" 
                                    label={'Vendor/Party *'} 
                                    size="small"
                                  />
                                )}
                                style={{ width: '100%' }}
                              />
                            )}
                          />
                        </Grid>
                        
                        {/* Amount */}
                        <Grid item xs={12} md={5}>
                          <CustomTextField 
                            label="Amount *" 
                            name="amount"
                            type="Number" 
                            variant="outlined" 
                            size='small'
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <Box sx={{ 
                                  mr: 0.5, 
                                  color: '#6366f1',
                                  fontWeight: 600 
                                }}>₹</Box>
                              )
                            }}
                          />
                        </Grid>

                        {/* Category and Date */}
                        <Grid item xs={12} md={7}>
                          <GroupedCategorySelect
                            name="expenseCategory"
                            label="Category *"
                            id="expenseCategory"
                            size="small"
                            categories={categories || []}
                            selectedValue={selectedExpenseCategory}
                            onChange={(event: SelectChangeEvent<any>) => {
                              setSelectedExpenseCategory(event.target.value);
                            }}
                            showNoneLabel
                            required
                            allowCreate={true}
                            onCategoryCreated={(newCategory) => {
                              dispatch(fetchExpenseCategories({ flat: false }));
                              setSelectedExpenseCategory(newCategory._id);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                              label="Date"
                              inputFormat="dd/MM/yyyy"
                              maxDate={moment().add(1).toDate()}
                              value={paymentDate}
                              onChange={(value) => { handleDateChange(value, 'paymentDate') }}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  name={'paymentDate'} 
                                  required 
                                  variant="outlined" 
                                  size='small' 
                                  fullWidth 
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </Grid>
                    </Box>
                  {/* </Box> */}
                </Grid>

                {/* Payment Method Section */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    backgroundColor: '#f3f3f3',
                    borderRadius: '8px',
                    p: 2,
                    pt:2,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  }}>
                    
                      <Grid container spacing={2} style={{paddingTop: '10px'}}>
                        <Grid item xs={12} md={6}>
                          <CustomSelect
                            sx={{
                              width: '100%',
                              '& .MuiInputLabel-root': {
                                transform: 'translate(14px, 9px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                  transform: 'translate(14px, -9px) scale(0.75)'
                                }
                              },
                              '& .MuiSelect-select': {
                                padding: '8.5px 14px',
                              }
                            }}
                            name="fromBankAccount"
                            label="From Account"
                            id="fromBankAccount-1"
                            variant="outlined"
                            size='small'
                            options={fromBankAccountOptions}
                            value={selectedFromBankAccount ?? ""}
                            onChange={(event: SelectChangeEvent<any>) => {
                              methods.setValue('fromBankAccount', event.target.value);
                              setSelectedFromBankAccount(event.target.value);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <CustomSelect
                            disabled={isCash}
                            sx={{
                              width: '100%',
                              '& .MuiInputLabel-root': {
                                transform: 'translate(14px, 9px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                  transform: 'translate(14px, -9px) scale(0.75)'
                                }
                              },
                              '& .MuiSelect-select': {
                                padding: '8.5px 14px',
                              }
                            }}
                            name="depositTo"
                            label="To Account"
                            id="depositTo"
                            variant="outlined"
                            size='small'
                            options={toBankAccountOptions}
                            value={selectedToBankAccount ?? ""}
                            onChange={(event: SelectChangeEvent<any>) => {
                              methods.setValue('toBankAccount', event.target.value);
                              setSelectedToBankAccount(event.target.value);
                            }}
                          />
                        </Grid>
                        {/* Payment Mode Radio Group */}
                        <Grid item xs={12}>
                          <RadioGroup
                            {...methods.register("instrumentType")}
                            row
                            aria-labelledby="payment-mode-label"
                            defaultValue="CASH"
                            name="instrumentType"
                            onChange={handleRadioChange}
                            sx={{
                              '& .MuiFormControlLabel-root': {
                                mr: 2,
                                ml: 0,
                                // pt:2,
                                '& .MuiRadio-root': {
                                  p: 0.5,
                                  mr: 0.5,
                                  color: '#6366f1',
                                  '&.Mui-checked': {
                                    color: '#6366f1',
                                  }
                                },
                                '& .MuiTypography-root': {
                                  fontSize: '0.875rem'
                                }
                              }
                            }}
                          >
                            <FormControlLabel value="CASH" control={<Radio size="small" />} label="Cash" />
                            <FormControlLabel value="CHEQUE" control={<Radio size="small" />} label="Cheque" />
                            <FormControlLabel value="CARD" control={<Radio size="small" />} label="Card" />
                            <FormControlLabel value="UPI" control={<Radio size="small" />} label="UPI" />
                            <FormControlLabel value="NETBANKING" control={<Radio size="small" />} label="Net Banking" />
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <CustomTextField
                            disabled={isCash}
                            variant="outlined"
                            size='small'
                            fullWidth
                            label={paymentMode === 'CHEQUE' ? "Cheque Number" : 
                                   paymentMode === 'UPI' ? "UPI ID" :
                                   paymentMode === 'CARD' ? "Card Ref" :
                                   "Transaction Ref"}
                            name="instrumentId"
                          />
                        </Grid>
                        <Grid item xs={12} md={6} style={{paddingTop: '33px'}}>
                          <CustomSelect
                            name="paymentStatus"
                            label="Status *"
                            id="paymentStatus"
                            size="small"
                            options={expenseStatusOptions}
                            value={expenseStatus}
                            onChange={(event: SelectChangeEvent<any>) => {
                              setExpenseStatus(event.target.value);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <CustomTextField 
                            label="Remarks" 
                            name="remark" 
                            variant="outlined" 
                            size='small'
                            fullWidth
                            multiline
                            rows={2}
                          />
                        </Grid>
                      </Grid>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ 
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              borderTop: '1px solid #c1c5cb',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
              padding: '16px 24px',
              margin: 0,
              zIndex: 1000
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: 2, 
                width: '100%' 
              }}>
                <Button 
                  variant='outlined'
                  onClick={() => {
                    methods.reset();
                    onClose(false);
                  }}
                  sx={{ 
                    minWidth: '100px',
                    height: '40px'
                  }}
                >
                  Cancel
                </Button>

                <LoadingButton
                  type="button"
                  onClick={onSubmit}
                  variant="contained"
                  disabled={!paymentToOrFromAccount}
                  loading={status === ApiStatus.LOADING}
                  sx={{ 
                    minWidth: '140px',
                    height: '40px'
                  }}
                >
                  Record Expense
                </LoadingButton>
              </Box>
            </DialogActions>

            
          </Box>
        </FormProvider>

        
      </div>
  );
};

export default DialogAddExpenseForm;